import { lazy } from "solid-js";
import { render } from "solid-js/web";
import { Router, Route } from "@solidjs/router";
import type { PublicSettingsOut } from "@saquei/types";
import "./styles.css";
import { DevPanel } from "./routes/dev";
import { applyBrand } from "./lib/brand";
import { bootstrapTheme } from "./lib/theme";
import { clearAuth, TENANT_AUTH_STORAGE_KEY } from "./lib/auth";
import { clearSuperAuth } from "./lib/super-auth";
import {
    installCrossTabAuthSync,
    installFetchAuthInterceptor,
    onAuthExpired,
} from "./lib/auth-fetch";
import { ConfirmDialogHost, toast, ToastViewport } from "./components/ui";

bootstrapTheme();

// Interceptor global de auth: 401, expiracao local, ou outra aba fazendo
// logout. Limpa storage, redireciona e avisa via toast. Sem isso, o usuario
// continuava "logado" na UI com tokens expirados gerando 401 mudos no console.
installFetchAuthInterceptor();
installCrossTabAuthSync({
    tenantStorageKey: TENANT_AUTH_STORAGE_KEY,
    superStorageKey: "saquei.super.token",
});

// Throttle de redirect: se varios requests dispararem 401 ao mesmo tempo,
// nao queremos navegar/clearAuth varias vezes nem mostrar toast duplicado.
let lastAuthExpiredAt = 0;
onAuthExpired(({ scope, reason }) => {
    const now = Date.now();
    if (now - lastAuthExpiredAt < 2000) return;
    lastAuthExpiredAt = now;

    if (scope === "tenant") {
        clearAuth();
    } else {
        clearSuperAuth();
    }

    const msg = reason === "cross_tab"
        ? "Você saiu em outra aba."
        : "Sua sessão expirou. Entre de novo.";
    toast.info(msg);

    // Hard navigate evita estados intermediarios -- garante reset completo
    // de signals locais que poderiam ainda ter o token antigo em memoria.
    const target = scope === "super" ? "/login" : "/admin/login";
    queueMicrotask(() => {
        if (window.location.pathname !== target) {
            window.location.assign(target);
        }
    });
});

// Tenant-only: carrega public-settings em paralelo e aplica brand antes que
// rotas pesadas terminem de hidratar. Em apex/super NAO tem tenant -> pular.
if (typeof window !== "undefined") {
    const parts = window.location.hostname.split(".");
    const isTenantHost =
        parts.length >= 3 && parts[0] !== "super" && parts[0] !== "www";
    if (isTenantHost) {
        fetch("/api/v1/tenant/public-settings")
            .then((r) => (r.ok ? r.json() : null))
            .then((out: PublicSettingsOut | null) => applyBrand(out))
            .catch(() => undefined);
    }
}

const HomeRoute = lazy(() => import("./routes/home"));
const FilaRoute = lazy(() => import("./routes/fila"));
const SessaoRoute = lazy(() => import("./routes/sessao"));
const SuaVezRoute = lazy(() => import("./routes/sua-vez"));
const PerfilRoute = lazy(() => import("./routes/perfil"));
const QrRoute = lazy(() => import("./routes/q"));
const AuthRoute = lazy(() => import("./routes/auth"));
const TvRoute = lazy(() => import("./routes/tv"));
const AdminLogin = lazy(() => import("./routes/admin-login"));
const Admin2faSetup = lazy(() => import("./routes/admin-2fa-setup"));
const Admin2faVerify = lazy(() => import("./routes/admin-2fa-verify"));
const AdminDashboard = lazy(() => import("./routes/admin"));
const AdminCourts = lazy(() => import("./routes/admin-courts"));
const AdminCourtsQrs = lazy(() => import("./routes/admin-courts-qrs"));
const AdminMembers = lazy(() => import("./routes/admin-members"));
const AdminSettings = lazy(() => import("./routes/admin-settings"));
const AdminTokens = lazy(() => import("./routes/admin-tokens"));
const AdminConta = lazy(() => import("./routes/admin-conta"));
const AdminAgenda = lazy(() => import("./routes/admin-agenda"));
const SuperLogin = lazy(() => import("./routes/super-login"));
const Super2faSetup = lazy(() => import("./routes/super-2fa-setup"));
const Super2faVerify = lazy(() => import("./routes/super-2fa-verify"));
const SuperDashboard = lazy(() => import("./routes/super-dashboard"));
const SuperTenants = lazy(() => import("./routes/super-tenants"));
const MarketingHome = lazy(() => import("./routes/marketing-home"));

// Detecta qual app servir: apex saquei.app (sem subdomain) -> marketing;
// super.saquei.app -> super-admin; *.saquei.app (tenant) -> tenant app.
function detectAppKind(): "marketing" | "super" | "tenant" {
    if (typeof window === "undefined") return "tenant";
    const host = window.location.hostname;
    const parts = host.split(".");
    if (parts.length < 2) return "marketing"; // localhost / dev
    // Heuristica: tem 2 partes (saquei.app) -> apex; primeira parte=super -> super.
    if (parts.length === 2) return "marketing";
    if (parts[0] === "super") return "super";
    if (parts[0] === "www") return "marketing";
    return "tenant";
}
const appKind = detectAppKind();

function TenantApp() {
    return (
        <Router>
            <Route path="/" component={HomeRoute} />
            <Route path="/fila" component={FilaRoute} />
            <Route path="/sessao" component={SessaoRoute} />
            <Route path="/sua-vez" component={SuaVezRoute} />
            <Route path="/perfil" component={PerfilRoute} />
            <Route path="/q/:code" component={QrRoute} />
            <Route path="/auth" component={AuthRoute} />
            <Route path="/dev" component={DevPanel} />
            <Route path="/tv" component={TvRoute} />
            <Route path="/admin/login" component={AdminLogin} />
            <Route path="/admin/2fa-setup" component={Admin2faSetup} />
            <Route path="/admin/2fa-verify" component={Admin2faVerify} />
            <Route path="/admin" component={AdminDashboard} />
            <Route path="/admin/courts" component={AdminCourts} />
            <Route path="/admin/courts/qrs" component={AdminCourtsQrs} />
            <Route path="/admin/members" component={AdminMembers} />
            <Route path="/admin/settings" component={AdminSettings} />
            <Route path="/admin/tokens" component={AdminTokens} />
            <Route path="/admin/conta" component={AdminConta} />
            <Route path="/admin/agenda" component={AdminAgenda} />
        </Router>
    );
}

function SuperApp() {
    return (
        <Router>
            <Route path="/login" component={SuperLogin} />
            <Route path="/2fa-setup" component={Super2faSetup} />
            <Route path="/2fa-verify" component={Super2faVerify} />
            <Route path="/" component={SuperDashboard} />
            <Route path="/tenants" component={SuperTenants} />
        </Router>
    );
}

function MarketingApp() {
    return (
        <Router>
            <Route path="/" component={MarketingHome} />
        </Router>
    );
}

function App() {
    if (appKind === "marketing") return <MarketingApp />;
    if (appKind === "super") return <SuperApp />;
    return <TenantApp />;
}

render(
    () => (
        <>
            <App />
            <ToastViewport />
            <ConfirmDialogHost />
        </>
    ),
    document.getElementById("app")!,
);
